import { restartableTask } from 'ember-concurrency';
import SimulationScenario, {
  Events,
  ScenarioResourceName,
  Validations as ScenarioValidations
} from 'volta/models/simulation-scenario';
import { normalize } from 'volta/utils/api/serialize-and-push';
import { defaultErrorHandler } from 'volta/utils/error-utils';

import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type BvMetricsService from 'volta/services/bv-metrics';
import type EventStreamService from 'volta/services/event-stream';
import type StoreService from 'volta/services/-store';
import type { ISimulationScenarioProgress } from 'volta/models/types/simulation';
import type { BufferedChangeset } from 'ember-changeset/types';
import type BvFlashService from 'volta/services/bv-flash';
import type IntlService from 'ember-intl/services/intl';
import type LabelService from 'volta/services/label-service';

interface IArgs {
  scenario: SimulationScenario;
  onCreateSuccess?: (scenario: SimulationScenario) => void;
}

export default class ScenarioItem extends Component<IArgs> {
  @service bvMetrics!: BvMetricsService;
  @service eventStream!: EventStreamService;
  @service store!: StoreService;
  @service bvFlash!: BvFlashService;
  @service intl!: IntlService;
  @service labelService!: LabelService;

  @tracked isEditing = false;
  @tracked calculationRunning = this.args.scenario.isScenarioLoading;
  @tracked calculationProgress?: number;
  @tracked backendErrors?: Record<string, string[] | null> = {};

  ScenarioValidations = ScenarioValidations;

  // Lifecycle Handlers
  // ~~~~~~

  constructor(owner: any, args: IArgs) {
    super(owner, args);
    this.eventStream.addHandler(
      ScenarioResourceName.plural,
      Events.SimulationScenarioCalculationDone,
      this.onScenarioDone,
      this
    );

    this.eventStream.addHandler(
      ScenarioResourceName.plural,
      Events.SimulationScenarioProgress,
      this.onScenarioProgress,
      this
    );

    this.eventStream.addHandler(
      ScenarioResourceName.plural,
      Events.SimulationScenarioCalculationStarted,
      this.onScenarioStarted,
      this
    );
  }

  willDestroy() {
    this.eventStream.removeHandlers(this);
  }

  updateScenario = restartableTask(async (changeset: BufferedChangeset, event: SubmitEvent) => {
    this.bvMetrics.trackAction(this, 'updateScenario');
    event.preventDefault();

    const { scenario } = this.args;
    changeset.validate();
    if (scenario && changeset && changeset.isValid) {
      const cmd = {
        name: changeset.name,
        forecastTypes: changeset.forecastTypes,
        useExplosion: changeset.useExplosion
      };
      try {
        const updatedScenario: SimulationScenario = await scenario.updateScenario(cmd);

        this.backendErrors = undefined;
        this.bvFlash.success(
          this.intl.t('bvList.singleActions.updateSuccess', {
            resourceNameSingular: ScenarioResourceName.singular,
            name: updatedScenario.name
          })
        );
        this.args.onCreateSuccess?.(updatedScenario);
        this.isEditing = false;
        return updatedScenario;
      } catch (error) {
        defaultErrorHandler(error);
        const backendErrors = this.labelService.formValidationsFromApiError(error);
        if (backendErrors) {
          this.backendErrors = backendErrors;
        } else {
          this.bvFlash.error(
            this.intl.t('bvList.singleActions.updateFail', {
              resourceNameSingular: ScenarioResourceName.singular,
              name: cmd.name
            })
          );
        }

        return error;
      }
    }
  });

  @action
  handleToggleEdition() {
    this.bvMetrics.trackAction(this, 'toggleEdit', `${!this.isEditing}`);
    this.isEditing = !this.isEditing;
  }

  private onScenarioProgress({ progress, scenarioId }: ISimulationScenarioProgress) {
    if (scenarioId === this.args.scenario.id && progress) {
      this.args.scenario.status = 'CALCULATING';
      const { total, calculated } = progress;
      this.calculationProgress = total ? calculated / total : undefined;
    }
  }

  private onScenarioDone({ scenarioId }: ISimulationScenarioProgress) {
    if (scenarioId === this.args.scenario.id) {
      this.args.scenario.status = 'DONE';
    }
  }

  private onScenarioStarted(data: { id: string }) {
    if (data.id === this.args.scenario.id) {
      this.args.scenario.status = 'CALCULATING';
      normalize(ScenarioResourceName.singular, { data });
      this.calculationProgress = 0;
    }
  }
}
